import { Box, Container, Flex, Grid, Heading } from '@chakra-ui/react';
import { StarBackground } from '@components/StarBackground';
import { YoutubeVideoWithPreviewImage } from '@components/YoutubePlayerModal';
import {
  AppLink,
  AppLinkWithArrow,
  BorderWithSquareDots,
  Card,
  fetchHttp,
  Image,
  LANDING_ROUTES,
  pxToRem,
  SHARED_ROUTES,
  TRANSITIONS,
  TwitterCommentIcon,
  TwitterIcon,
  TwitterLikeIcon,
  TwitterRetweetIcon,
} from '@cyberkongz/ui';
import { SliderNavigationRounded, SliderWrapper } from '@cyberkongz/ui-slider';
import {
  ExtractedPostWithAssets,
  getAllCategories,
  getAllPodcasts,
  getAllPosts,
} from '@features/blog/apiContentful';
import { BlogPostsTiles } from '@features/blog/BlogPostsTiles';
import { BlogSearch } from '@features/blog/BlogSearch';
import {
  DEFAULT_BLOG_REVALIDATE_PERIOD,
  formatDate,
  getFeaturedImageSmall,
} from '@features/blog/utils';
import { PageSEOTags } from '@features/seo/SEO';
import {
  APILatestTweetsResponse,
  TweetData,
  TwitterMediaFile,
  TwitterUser,
} from '@pages/api/latest-tweets';
import { useQuery } from '@tanstack/react-query';
import { IBlogCategoryFields, IPodcastsFields } from '@typings/generated/contentful';
import { InferGetStaticPropsType } from 'next';
import { ReactNode, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

function IntroSection({
  posts,
  post: { dateCreated, title, excerpt, featuredImage, slug },
}: {
  posts: ExtractedPostWithAssets[];
  post: ExtractedPostWithAssets;
}) {
  return (
    <Box
      css={{
        pt: [pxToRem(120), pxToRem(140)],
        pb: [10, 20],
        bg: `url(/static/blog/intro_bg.png) center bottom/cover no-repeat`,
      }}
    >
      <Container>
        <Flex
          css={{
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: [10, 16],
            gap: 4,
            flexDirection: ['column', 'row'],
          }}
        >
          <Heading variant="displayPrimary" css={{ fontSize: ['5xl', '7xl'] }}>
            What&apos;s happening
          </Heading>
          <BlogSearch {...{ posts }} />
        </Flex>
        <Flex css={{ gap: 10, alignItems: 'center', flexDirection: ['column', 'row'] }}>
          <Box css={{ color: 'textSecondary', flex: 1 }}>
            <Box css={{ fontSize: 'sm', fontWeight: 'medium', color: 'textTertiary', mb: 2 }}>
              {formatDate(dateCreated)}
            </Box>
            <AppLink href={LANDING_ROUTES.BLOG_POST(slug)}>
              <Heading size="lg" css={{ mb: 4 }}>
                {title}
              </Heading>
            </AppLink>
            <Box css={{ lineHeight: 'tall' }}>{excerpt}</Box>
          </Box>
          {featuredImage ? (
            <Image
              css={{ order: [-1, 1], maxWidth: ['100%', '50%', pxToRem(604)] }}
              src={featuredImage.fields.file?.url}
              alt={featuredImage.fields.title}
            />
          ) : null}
        </Flex>
      </Container>
    </Box>
  );
}

function LatestNewsSection({ posts }: { posts: ExtractedPostWithAssets[] }) {
  return (
    <Box css={{ pt: [16, 20], pb: [20, pxToRem(112)] }}>
      <Container position="relative" zIndex={1}>
        <Heading size="sm">latest news</Heading>
        <Grid
          css={{
            mt: [10, 12],
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: [3, 8, 16],
            '@media screen and (max-width: 420px)': {
              gridTemplateColumns: 'repeat(1, 1fr)',
              rowGap: 8,
            },
          }}
        >
          {posts.map(({ dateCreated, title, featuredImage, slug }) => (
            <AppLink key={slug} href={LANDING_ROUTES.BLOG_POST(slug)}>
              <Flex
                css={{
                  alignItems: ['flex-start', 'center'],
                  flexDirection: ['column', 'row'],
                }}
              >
                <Image
                  src={getFeaturedImageSmall(featuredImage?.fields.file?.url)}
                  alt={featuredImage?.fields.title}
                  css={{
                    mr: [0, 4, 8],
                    width: '100%',
                    maxWidth: ['100%', pxToRem(178)],
                  }}
                />
                <Box css={{ fontWeight: 'medium', mt: [4, 0] }}>
                  <Box css={{ fontSize: 'xs', color: 'textTertiary', mb: 1 }}>
                    {formatDate(dateCreated)}
                  </Box>
                  <Box>{title}</Box>
                </Box>
              </Flex>
            </AppLink>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

function LatestPodcatsSection({ podcasts }: { podcasts: IPodcastsFields[] }) {
  return (
    <Box
      css={{
        pt: [16, 20],
        pb: [20, pxToRem(112)],
        bg: `url(/static/blog/podcasts_bg.jpg) center/cover no-repeat`,
      }}
    >
      <Container>
        <Heading size="sm" css={{ textAlign: ['left', 'center'], mb: [8, 12] }}>
          latest podcasts
        </Heading>
        <SliderWrapper
          css={{
            '& .swiper-slide': {
              transition: TRANSITIONS.standard,
              transform: 'scale(0.8)',

              '& .description': {
                opacity: 0,
                transition: TRANSITIONS.standard,
              },
            },

            '& .swiper-slide-active': {
              transform: 'scale(1.0)',

              '& .description': {
                opacity: 1,
              },
            },
          }}
          centeredSlides
          slidesPerView={1.25}
          loop
          roundLengths
          spaceBetween={1}
          breakpoints={{
            768: {
              slidesPerView: 2,
            },
          }}
        >
          {podcasts.map(({ dateCreated, title, link }) => (
            <SwiperSlide key={title} style={{ width: pxToRem(500) }}>
              <Box
                css={{ maxWidth: pxToRem(604), fontWeight: 'medium', textTransform: 'uppercase' }}
              >
                <YoutubeVideoWithPreviewImage src={link} />
                <Box className="description">
                  <Box css={{ mt: 8, mb: 2, fontSize: 'sm', color: 'textTertiary' }}>
                    {formatDate(dateCreated)}
                  </Box>
                  <Box css={{ fontSize: ['md', 'xl'], color: 'textSecondary' }}>{title}</Box>
                </Box>
              </Box>
            </SwiperSlide>
          ))}
          <SliderNavigationRounded />
        </SliderWrapper>
      </Container>
    </Box>
  );
}

// TO DO ADD PODCASTS LOGIC IN FUTURE :HMM:
function CategoriesSection({
  categories,
  posts,
}: {
  categories: IBlogCategoryFields[];
  posts: ExtractedPostWithAssets[];
}) {
  const [activeCategory, setActiveCategory] = useState('/all');
  const postsToDisplay =
    activeCategory === '/all'
      ? posts
      : posts.filter((post) =>
          post.categories?.some((category) => category.fields.slug === activeCategory)
        );

  return (
    <Box css={{ pt: [16, 20], pb: [20, pxToRem(112)], position: 'relative' }}>
      <Container>
        <Heading size="sm">CATEGORIES</Heading>
        <SliderWrapper
          css={{
            alignItems: 'center',
            fontSize: 'sm',
            color: 'textSecondary',
            mt: [4, 6, 8],
            mb: [10, 16],
            '& .swiper-slide': {
              width: 'auto',
            },
          }}
          slidesPerView="auto"
          spaceBetween={16}
        >
          {categories.map(({ title, slug }) => (
            <SwiperSlide key={slug}>
              <Box
                css={{
                  py: 2,
                  px: 4,
                  minWidth: pxToRem(62),
                  border: 'thin',
                  borderRadius: '3em',
                  cursor: 'pointer',
                  textAlign: 'center',
                  borderColor: activeCategory === slug ? 'primary' : 'textSecondary',
                  color: activeCategory === slug ? 'primary' : 'inherit',
                  transition: TRANSITIONS.standard,
                }}
                onClick={() => setActiveCategory(slug)}
              >
                {title}
              </Box>
            </SwiperSlide>
          ))}
        </SliderWrapper>
        <BlogPostsTiles posts={postsToDisplay} />
      </Container>
    </Box>
  );
}

const USERNAME = {
  username: 'CyberKongz',
  id: '1367095024069062657',
  profile_image_url: 'https://pbs.twimg.com/profile_images/1615258217818341377/k2vK1Sz8_normal.jpg',
  name: 'CyberKongz',
};

const TweetsData = {
  latestTweets: {
    includes: {
      users: [
        {
          username: 'CyberKongz',
          id: '1367095024069062657',
          profile_image_url:
            'https://pbs.twimg.com/profile_images/1615258217818341377/k2vK1Sz8_normal.jpg',
          name: 'CyberKongz',
        },
      ],
      media: [
        {
          type: 'photo',
          url: 'https://pbs.twimg.com/media/FqDhVZPWwAAFUDp.jpg',
          media_key: '3_1630550822776913920',
          height: 910,
        },
      ],
    },
    data: [
      {
        text: '🚨Join us tomorrow for a community spaces with @HenryTheGrape and @enzoblue!\n\n🗓️Wed. 5 PM UTC/ 12 PM EST\n https://t.co/MzER42bpfh',
        created_at: '2023-02-28T18:10:05.000Z',
        id: '1630631439459860488',
        edit_history_tweet_ids: ['1630631439459860488'],
        author_id: '1367095024069062657',
        public_metrics: {
          retweet_count: 12,
          reply_count: 10,
          like_count: 28,
          quote_count: 0,
          impression_count: 2083,
        },
      },
      {
        attachments: {
          media_keys: ['3_1630550822776913920'],
        },
        text: '🚨Less than 24 hours until Play &amp; Kollect Season 34 ends!\n\nCurrent $BANANA prize pool is 36,812.8 🍌 \n\nMake sure to use any remaining fuel rods! https://t.co/jNtIh3V1HI',
        created_at: '2023-02-28T12:49:49.000Z',
        id: '1630550843760996354',
        edit_history_tweet_ids: ['1630550843760996354'],
        author_id: '1367095024069062657',
        public_metrics: {
          retweet_count: 11,
          reply_count: 9,
          like_count: 83,
          quote_count: 0,
          impression_count: 4138,
        },
      },
      {
        text: 'Come hang with @HenryTheGrape and @enzoblue!\n\nhttps://t.co/kLumEj5zZf',
        created_at: '2023-02-22T16:59:53.000Z',
        id: '1628439445119565824',
        edit_history_tweet_ids: ['1628439445119565824'],
        author_id: '1367095024069062657',
        public_metrics: {
          retweet_count: 6,
          reply_count: 0,
          like_count: 13,
          quote_count: 0,
          impression_count: 1655,
        },
      },
    ],
  },
};

function TwitterMetric({ icon, number }: { icon: ReactNode; number: number }) {
  return (
    <Flex alignItems="center" gap={1}>
      {icon}
      <Box>{number}</Box>
    </Flex>
  );
}

function TestTweet({
  user,
  tweet,
  media,
}: {
  user: TwitterUser;
  tweet: TweetData;
  media: TwitterMediaFile[];
}) {
  const mediaIndex = media.findIndex(
    (mediaFile) =>
      mediaFile.type === 'photo' &&
      tweet.attachments?.media_keys.some((tweetMediaFile) => tweetMediaFile === mediaFile.media_key)
  );

  return (
    <Card
      borderRadius="none"
      p={[4, 8]}
      height="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={3}>
            <Image borderRadius="50%" src={user.profile_image_url} />
            <AppLink href={SHARED_ROUTES.TWITTER}>
              <Box>
                <Box fontWeight="bold" position="relative" transform="translateX(-0.3em)">
                  {user.name}
                </Box>
                <Box>@{user.username}</Box>
              </Box>
            </AppLink>
          </Flex>
          <AppLink href={`https://twitter.com/CyberKongz/status/${tweet.id}`}>
            <TwitterIcon color="#4D9FEB" />
          </AppLink>
        </Flex>
        <Box mt={4} textAlign="left">
          {tweet.text}
        </Box>
        {mediaIndex > -1 ? <Image mt={6} src={media[mediaIndex].url} alt="Tweet Image" /> : null}
      </Box>
      <Flex alignItems="center" justifyContent="space-between" mt={8}>
        <Flex fontSize="sm" fontWeight="medium" gap={5}>
          <TwitterMetric icon={<TwitterCommentIcon />} number={tweet.public_metrics.reply_count} />
          <TwitterMetric
            icon={<TwitterRetweetIcon />}
            number={tweet.public_metrics.retweet_count}
          />
          <TwitterMetric icon={<TwitterLikeIcon />} number={tweet.public_metrics.like_count} />
        </Flex>
        <Box fontWeight="medium">{formatDate(tweet.created_at)}</Box>
      </Flex>
    </Card>
  );
}

// TO DO NICE add dots for carousel??
function TwitterSection() {
  const { data } = useQuery({
    queryKey: ['/api/latest-tweets'],
    queryFn: () => fetchHttp<APILatestTweetsResponse>('/api/latest-tweets'),

    retry: false,
  });
  const latestTweets = data?.latestTweets;

  return (
    <>
      <Box position="relative">
        <Container>
          <BorderWithSquareDots />
        </Container>
      </Box>
      <Box
        css={{
          pt: [16, 20],
          pb: [20, pxToRem(112)],
          position: 'relative',
          bg: 'linear-gradient(180deg, rgba(0, 0, 0, 0) -9.13%, rgba(255, 0, 229, 0.14) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7))',
        }}
      >
        <Container>
          <Heading pb={12} size="sm">
            follow along on
            <Heading
              as="span"
              size="sm"
              variant="displaySecondary"
              css={{
                ml: 4,
                position: 'relative',
                top: '0.07em',
                fontSize: [pxToRem(36), '4xl'],
              }}
            >
              Twitter
            </Heading>
          </Heading>
          <SliderWrapper
            breakpoints={{
              480: {
                slidesPerView: 1.25,
                spaceBetween: 10,
              },
              768: {
                slidesPerView: 2.25,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 32,
              },
            }}
            css={{ '& .swiper-slide': { textAlign: 'center', height: 'auto' } }}
          >
            {data && latestTweets
              ? latestTweets.data.map((tweet) => (
                  <SwiperSlide key={tweet.id}>
                    <TestTweet
                      user={latestTweets.includes.users[0]}
                      tweet={tweet}
                      media={TweetsData.latestTweets.includes.media}
                    />
                  </SwiperSlide>
                ))
              : null}
          </SliderWrapper>
          <Flex css={{ justifyContent: 'center', pt: [8, 12] }}>
            <AppLinkWithArrow href={SHARED_ROUTES.TWITTER}>View More on Twitter</AppLinkWithArrow>
          </Flex>
        </Container>
      </Box>
    </>
  );
}

export default function BlogPage({
  posts,
  podcasts,
  categories,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <Box css={{ width: '100%' }}>
      <PageSEOTags pageKey="blog" />
      <IntroSection posts={posts} post={posts[0]} />
      <StarBackground>
        <LatestNewsSection posts={posts.slice(0, 7)} />
      </StarBackground>
      <LatestPodcatsSection podcasts={podcasts} />
      <StarBackground>
        <CategoriesSection {...{ categories, posts: posts }} />
        <TwitterSection />
      </StarBackground>
    </Box>
  );
}

export async function getStaticProps() {
  const posts = await getAllPosts();
  const podcasts = await getAllPodcasts();
  const categories = await getAllCategories();

  return {
    props: {
      posts,
      podcasts,
      // add All category artificialy
      categories: [{ title: 'All', slug: '/all' }, ...categories],
    },
    revalidate: DEFAULT_BLOG_REVALIDATE_PERIOD,
  };
}
